import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Users, Coins, Wallet, TrendingUp, ArrowLeft, ChevronRight, Webhook, RefreshCw, CheckCircle2, XCircle, Filter, Database, Star, MessageSquare, Trash2 } from "lucide-react";
import Navbar from "@/components/Navbar";
import api, { formatApiError } from "@/lib/api";

function fmtINR(paise) {
  if (paise == null) return "₹0";
  return "₹" + Math.round(paise / 100).toLocaleString("en-IN");
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch { return iso; }
}

function StatCard({ label, value, sub, icon: Icon, tone = "black" }) {
  const bg = tone === "blue" ? "bg-[#DBEAFE]" : "bg-white";
  const border = tone === "blue" ? "border-black" : "border-black/20";
  return (
    <div className={`border-2 ${border} ${bg} p-5`}>
      <div className="flex items-center justify-between">
        <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{label}</p>
        {Icon && <Icon className="w-4 h-4 text-[#3B82F6]" />}
      </div>
      <p className="mt-2 font-heading font-black text-3xl">{value}</p>
      {sub && <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-zinc-500">{sub}</p>}
    </div>
  );
}

function UserDetail({ userId, onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/admin/users/${userId}`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [userId]);

  if (loading) return <p className="mt-6 font-mono text-xs text-zinc-500">Loading…</p>;
  if (!data) return null;
  const s = data.subscription;
  const q = s?.quota;

  return (
    <div>
      <button
        data-testid="admin-back"
        onClick={onBack}
        className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-zinc-600 hover:text-black"
      >
        <ArrowLeft className="w-3.5 h-3.5" /> Back to users
      </button>

      <div className="mt-4 border-2 border-black bg-white p-6">
        <div className="flex items-baseline justify-between flex-wrap gap-2">
          <h2 className="font-heading font-black text-2xl">{data.user.name || data.user.email}</h2>
          <span className="font-mono text-xs text-zinc-500">{data.user.email}</span>
        </div>
        <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-zinc-500">
          Joined {fmtDate(data.user.created_at)} · {data.notes_total} notes generated
        </p>

        {s ? (
          <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatCard label="Plan" value={s.plan?.toUpperCase() || "STANDARD"} sub={s.status} />
            <StatCard label="Included used" value={`${q?.included_used}/${q?.included_total}`} sub={`${q?.included_remaining} left`} />
            <StatCard label="Extra bought" value={q?.extra_purchased || 0} sub={`${q?.extra_remaining} unused`} />
            <StatCard label="Cycle ends" value={fmtDate(s.cycle_end)} sub={fmtDate(s.cycle_start) + " →"} />
          </div>
        ) : (
          <p className="mt-4 text-sm text-zinc-500">No active subscription.</p>
        )}
      </div>

      <div className="mt-6 grid md:grid-cols-2 gap-6">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">/ Subscription payments</p>
          <div className="mt-3 border border-black/20 bg-white divide-y divide-black/10">
            {(data.subscription_payments || []).length === 0 && (
              <p className="p-4 text-sm text-zinc-500">No payments</p>
            )}
            {(data.subscription_payments || []).map((p) => (
              <div key={p.id} className="p-4 flex items-center justify-between text-sm">
                <div>
                  <p className="font-medium">Standard plan</p>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                    {fmtDate(p.purchased_at)}
                  </p>
                </div>
                <p className="font-heading font-bold">{fmtINR(p.amount_paise)}</p>
              </div>
            ))}
          </div>
        </div>

        <div>
          <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">/ Credit purchases</p>
          <div className="mt-3 border border-black/20 bg-white divide-y divide-black/10">
            {(data.credit_purchases || []).length === 0 && (
              <p className="p-4 text-sm text-zinc-500">No credit purchases</p>
            )}
            {(data.credit_purchases || []).map((c) => (
              <div key={c.id} className="p-4 flex items-center justify-between text-sm">
                <div>
                  <p className="font-medium">+{c.videos} videos</p>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                    {fmtDate(c.purchased_at)}
                  </p>
                </div>
                <p className="font-heading font-bold">{fmtINR(c.amount_paise)}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function WebhookInspector() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [failuresOnly, setFailuresOnly] = useState(false);

  const load = () => {
    setLoading(true);
    api
      .get("/admin/webhooks?limit=100")
      .then((r) => setRows(r.data.webhooks || []))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const isFailure = (w) => !w?.event || w.event.includes("failed");
  const failureCount = rows.filter(isFailure).length;
  const visibleRows = failuresOnly ? rows.filter(isFailure) : rows;

  return (
    <section data-testid="webhook-inspector" className="mt-12">
      <div className="flex items-baseline justify-between flex-wrap gap-3">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">/ Razorpay</p>
          <h2 className="font-heading font-black text-2xl mt-1">Webhook inbox</h2>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="webhook-filter-failures"
            onClick={() => { setFailuresOnly((v) => !v); setOpenId(null); }}
            aria-pressed={failuresOnly}
            title={failuresOnly ? "Show all webhooks" : "Show only failed webhooks"}
            className={`inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest px-3 py-2 border transition-colors ${
              failuresOnly
                ? "bg-red-600 text-white border-red-600"
                : "bg-white text-black border-black/20 hover:bg-black/5"
            }`}
          >
            <Filter className="w-3.5 h-3.5" />
            {failuresOnly ? "Failures only · on" : "Failures only"}
            <span
              className={`ml-1 px-1.5 py-0.5 text-[9px] leading-none rounded-sm ${
                failuresOnly ? "bg-white text-red-600" : "bg-red-600 text-white"
              }`}
            >
              {failureCount}
            </span>
          </button>
          <button
            onClick={load}
            className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest px-3 py-2 hover:bg-black/5"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
      </div>

      {loading && <p className="mt-4 font-mono text-xs text-zinc-500">Loading…</p>}

      {!loading && rows.length === 0 && (
        <div className="mt-4 border-2 border-dashed border-black/30 p-8 text-center">
          <Webhook className="w-8 h-8 mx-auto text-zinc-400" />
          <p className="mt-3 font-heading font-bold">No webhooks received yet</p>
          <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-zinc-500">
            Trigger a test webhook from the Razorpay dashboard → Settings → Webhooks
          </p>
        </div>
      )}

      {!loading && rows.length > 0 && failuresOnly && visibleRows.length === 0 && (
        <div
          data-testid="webhook-no-failures"
          className="mt-4 border-2 border-emerald-300 bg-emerald-50 p-6 text-center"
        >
          <CheckCircle2 className="w-8 h-8 mx-auto text-emerald-600" />
          <p className="mt-2 font-heading font-bold text-emerald-900">All clear — no failed webhooks</p>
          <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-emerald-800">
            {rows.length} recent event{rows.length === 1 ? "" : "s"}, all successful
          </p>
        </div>
      )}

      {!loading && visibleRows.length > 0 && (
        <div className="mt-4 border-2 border-black bg-white divide-y divide-black/10">
          {visibleRows.map((w, i) => {
            const isOpen = openId === i;
            const success = !isFailure(w);
            return (
              <div key={i}>
                <button
                  data-testid={`webhook-row-${i}`}
                  onClick={() => setOpenId(isOpen ? null : i)}
                  className="w-full text-left p-4 hover:bg-[#DBEAFE]/40 transition-colors flex items-center justify-between gap-4"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    {success ? (
                      <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                    ) : (
                      <XCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
                    )}
                    <div className="min-w-0">
                      <p className="font-mono text-xs">
                        <span className="font-bold">{w.event}</span>
                      </p>
                      <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 truncate">
                        {w._ref?.razorpay_order_id || w._ref?.razorpay_payment_id || "—"}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 flex-shrink-0 text-right">
                    {w._ref?.amount != null && (
                      <span className="font-mono text-xs">
                        ₹{Math.round(w._ref.amount / 100).toLocaleString("en-IN")}
                      </span>
                    )}
                    <span className="hidden sm:inline font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                      {new Date(w.received_at).toLocaleString()}
                    </span>
                    <ChevronRight
                      className={`w-4 h-4 text-zinc-400 transition-transform ${isOpen ? "rotate-90" : ""}`}
                    />
                  </div>
                </button>
                {isOpen && (
                  <pre className="p-4 bg-black text-emerald-300 font-mono text-[10px] overflow-x-auto whitespace-pre-wrap">
                    {JSON.stringify(w.payload, null, 2)}
                  </pre>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

/* -------------------------------------------------------------------------
 * DatabaseBrowser — read-only Mongo inspector for admins.
 * ------------------------------------------------------------------------- */
function DatabaseBrowser() {
  const [collections, setCollections] = useState([]);
  const [dbName, setDbName] = useState("");
  const [collectionsLoading, setCollectionsLoading] = useState(true);
  const [active, setActive] = useState(null); // collection name
  const [docs, setDocs] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [pageLoading, setPageLoading] = useState(false);
  const [openIdx, setOpenIdx] = useState(null);
  const LIMIT = 25;

  useEffect(() => {
    setCollectionsLoading(true);
    api
      .get("/admin/database/collections")
      .then((r) => {
        setCollections(r.data.collections || []);
        setDbName(r.data.database || "");
      })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setCollectionsLoading(false));
  }, []);

  const loadCollection = (name, newSkip = 0) => {
    setPageLoading(true);
    setActive(name);
    setSkip(newSkip);
    setOpenIdx(null);
    api
      .get(`/admin/database/${encodeURIComponent(name)}?limit=${LIMIT}&skip=${newSkip}`)
      .then((r) => {
        setDocs(r.data.documents || []);
        setTotal(r.data.total || 0);
      })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setPageLoading(false));
  };

  return (
    <section data-testid="db-browser" className="mt-10 grid lg:grid-cols-[280px_1fr] gap-6">
      {/* Collections sidebar */}
      <aside className="border-2 border-black bg-white">
        <div className="border-b-2 border-black bg-black text-white px-3 py-2">
          <p className="font-mono text-[10px] uppercase tracking-widest">
            Database · <span className="text-[#BFDBFE]">{dbName || "…"}</span>
          </p>
        </div>
        {collectionsLoading ? (
          <p className="p-4 font-mono text-xs text-zinc-500">Loading collections…</p>
        ) : collections.length === 0 ? (
          <p className="p-4 font-mono text-xs text-zinc-500">No collections.</p>
        ) : (
          <ul className="divide-y divide-black/10 max-h-[70vh] overflow-auto">
            {collections.map((c) => (
              <li key={c.name}>
                <button
                  data-testid={`db-collection-${c.name}`}
                  onClick={() => loadCollection(c.name)}
                  className={`w-full text-left px-3 py-2 flex items-center justify-between hover:bg-[#DBEAFE]/40 ${
                    active === c.name ? "bg-[#DBEAFE]" : ""
                  }`}
                >
                  <span className="font-mono text-xs truncate">{c.name}</span>
                  <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                    {c.count.toLocaleString("en-IN")}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </aside>

      {/* Documents pane */}
      <div>
        {!active && (
          <div className="border-2 border-dashed border-black/30 p-8 text-center">
            <Database className="w-8 h-8 mx-auto text-zinc-400" />
            <p className="mt-3 font-heading font-bold">Pick a collection</p>
            <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-zinc-500">
              Choose a collection on the left to browse recent documents (newest first).
            </p>
          </div>
        )}
        {active && (
          <>
            <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                  / Collection
                </p>
                <h3 className="font-heading font-black text-2xl">{active}</h3>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                  {total.toLocaleString("en-IN")} docs
                </span>
                <button
                  data-testid="db-prev"
                  onClick={() => loadCollection(active, Math.max(0, skip - LIMIT))}
                  disabled={skip === 0 || pageLoading}
                  className="font-mono text-[10px] uppercase tracking-widest px-2 py-1 border border-black/20 disabled:opacity-40"
                >
                  Prev
                </button>
                <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                  {skip + 1}–{Math.min(skip + docs.length, total)} of {total}
                </span>
                <button
                  data-testid="db-next"
                  onClick={() => loadCollection(active, skip + LIMIT)}
                  disabled={skip + LIMIT >= total || pageLoading}
                  className="font-mono text-[10px] uppercase tracking-widest px-2 py-1 border border-black/20 disabled:opacity-40"
                >
                  Next
                </button>
                <button
                  data-testid="db-refresh"
                  onClick={() => loadCollection(active, skip)}
                  className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest px-2 py-1 hover:bg-black/5"
                >
                  <RefreshCw className={`w-3 h-3 ${pageLoading ? "animate-spin" : ""}`} />
                  Refresh
                </button>
              </div>
            </div>

            {pageLoading && (
              <p className="font-mono text-xs text-zinc-500">Loading documents…</p>
            )}

            {!pageLoading && docs.length === 0 && (
              <div className="border-2 border-dashed border-black/30 p-6 text-center">
                <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                  Collection is empty.
                </p>
              </div>
            )}

            {!pageLoading && docs.length > 0 && (
              <div className="border-2 border-black bg-white divide-y divide-black/10">
                {docs.map((d, i) => {
                  const isOpen = openIdx === i;
                  // Summarise the doc: id/email/title/created_at
                  const summary =
                    d.email ||
                    d.title ||
                    d.id ||
                    d.order_id ||
                    d.event_id ||
                    "(document)";
                  return (
                    <div key={i}>
                      <button
                        data-testid={`db-doc-${i}`}
                        onClick={() => setOpenIdx(isOpen ? null : i)}
                        className="w-full text-left p-3 flex items-center justify-between hover:bg-[#DBEAFE]/40 gap-4"
                      >
                        <span className="font-mono text-xs truncate">{summary}</span>
                        <span className="flex items-center gap-2 flex-shrink-0">
                          {d.created_at && (
                            <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                              {new Date(d.created_at).toLocaleString()}
                            </span>
                          )}
                          <ChevronRight
                            className={`w-4 h-4 text-zinc-400 transition-transform ${
                              isOpen ? "rotate-90" : ""
                            }`}
                          />
                        </span>
                      </button>
                      {isOpen && (
                        <pre className="p-4 bg-black text-emerald-300 font-mono text-[10px] overflow-x-auto whitespace-pre-wrap max-h-[500px] overflow-y-auto">
                          {JSON.stringify(d, null, 2)}
                        </pre>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}



function ReviewsAdmin() {
  const [status, setStatus] = useState("pending");
  const [reviews, setReviews] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, approved: 0, rejected: 0 });
  const [loading, setLoading] = useState(true);

  const load = async (s = status) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/reviews`, { params: { status: s, limit: 200 } });
      setReviews(data.items || []);
      setCounts(data.counts || { pending: 0, approved: 0, rejected: 0 });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(status); }, [status]);

  const setReviewStatus = async (id, newStatus) => {
    try {
      await api.post(`/admin/reviews/${id}/status`, { status: newStatus });
      toast.success(newStatus === "approved" ? "Approved" : newStatus === "rejected" ? "Rejected" : "Moved to pending");
      load(status);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const removeReview = async (id) => {
    if (!window.confirm("Delete this review permanently?")) return;
    try {
      await api.delete(`/admin/reviews/${id}`);
      toast.success("Review deleted");
      load(status);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const stars = (n) => "★".repeat(Math.max(0, Math.min(5, n))).padEnd(5, "☆");

  return (
    <section data-testid="admin-reviews" className="mt-10 space-y-6">
      <div className="flex flex-wrap items-center gap-2">
        {[
          { id: "pending", label: `Pending · ${counts.pending}` },
          { id: "approved", label: `Approved · ${counts.approved}` },
          { id: "rejected", label: `Rejected · ${counts.rejected}` },
          { id: "all", label: "All" },
        ].map((t) => (
          <button
            key={t.id}
            data-testid={`reviews-filter-${t.id}`}
            onClick={() => setStatus(t.id)}
            className={`inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest px-3 py-2 border ${
              status === t.id ? "bg-black text-white border-black" : "bg-white border-black/20 hover:bg-black/5"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="font-mono text-xs text-zinc-500">Loading reviews…</p>
      ) : reviews.length === 0 ? (
        <p className="font-mono text-xs text-zinc-500">No reviews in this bucket.</p>
      ) : (
        <div className="space-y-3">
          {reviews.map((r) => (
            <div
              key={r.id}
              data-testid={`admin-review-${r.id}`}
              className="border-2 border-black bg-white p-4 shadow-[3px_3px_0_0_#000]"
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-mono text-sm text-[#FBBF24] tracking-wider">{stars(r.rating)}</span>
                    <span className="font-heading font-bold">{r.name}</span>
                    <span className="font-mono text-[10px] text-zinc-500">{r.email}</span>
                    <span
                      className={`font-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 border ${
                        r.scope === "app"
                          ? "bg-[#DBEAFE] border-black/20"
                          : "bg-[#FEF3C7] border-black/20"
                      }`}
                    >
                      {r.scope}
                    </span>
                    <span className="font-mono text-[10px] text-zinc-400">{fmtDate(r.created_at)}</span>
                  </div>
                  {r.text && <p className="mt-2 text-sm text-zinc-800">&ldquo;{r.text}&rdquo;</p>}
                  {r.scope === "note" && r.target_id && (
                    <p className="mt-1 font-mono text-[10px] text-zinc-500">note: {r.target_id}</p>
                  )}
                </div>
                <div className="flex gap-2 flex-wrap">
                  {r.status !== "approved" && (
                    <button
                      data-testid={`review-approve-${r.id}`}
                      onClick={() => setReviewStatus(r.id, "approved")}
                      className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest px-2 py-1.5 border-2 border-[#3B82F6] text-[#3B82F6] hover:bg-[#3B82F6] hover:text-white transition-colors"
                    >
                      <CheckCircle2 className="w-3 h-3" /> Approve
                    </button>
                  )}
                  {r.status !== "rejected" && (
                    <button
                      data-testid={`review-reject-${r.id}`}
                      onClick={() => setReviewStatus(r.id, "rejected")}
                      className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest px-2 py-1.5 border border-black/40 hover:bg-black hover:text-white transition-colors"
                    >
                      <XCircle className="w-3 h-3" /> Reject
                    </button>
                  )}
                  <button
                    data-testid={`review-delete-${r.id}`}
                    onClick={() => removeReview(r.id)}
                    className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest px-2 py-1.5 border border-[#FF3333]/40 text-[#FF3333] hover:bg-[#FF3333] hover:text-white transition-colors"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}


export default function Admin() {
  const [overview, setOverview] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [showWebhooks, setShowWebhooks] = useState(false);
  const [showDatabase, setShowDatabase] = useState(false);
  const [showReviews, setShowReviews] = useState(false);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get("/admin/overview").then((r) => r.data),
      api.get("/admin/users").then((r) => r.data),
    ])
      .then(([o, u]) => { setOverview(o); setUsers(u); })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F0F9FF]">
        <Navbar />
        <main className="max-w-6xl mx-auto px-6 py-12">
          <p className="font-mono text-xs text-zinc-500">Loading admin dashboard…</p>
        </main>
      </div>
    );
  }

  const totalRev = (overview?.subscription_revenue_paise || 0) + (overview?.credits_revenue_paise || 0);

  return (
    <div className="min-h-screen bg-[#F0F9FF]">
      <Navbar />
      <main className="max-w-6xl mx-auto px-6 py-12">
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">/ Admin</p>
        <div className="mt-2 flex items-baseline justify-between flex-wrap gap-3">
          <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter">
            {showReviews ? "Reviews" : showDatabase ? "Database" : showWebhooks ? "Webhook inspector" : "Revenue & users"}
          </h1>
          <div className="flex gap-2 flex-wrap">
            <button
              data-testid="tab-admin-overview"
              onClick={() => { setShowWebhooks(false); setShowDatabase(false); setShowReviews(false); setSelectedUserId(null); }}
              className={`inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest px-3 py-2 border ${!showWebhooks && !showDatabase && !showReviews ? "bg-black text-white border-black" : "bg-white border-black/20 hover:bg-black/5"}`}
            >
              <Wallet className="w-3.5 h-3.5" /> Overview
            </button>
            <button
              data-testid="tab-admin-webhooks"
              onClick={() => { setShowWebhooks(true); setShowDatabase(false); setShowReviews(false); setSelectedUserId(null); }}
              className={`inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest px-3 py-2 border ${showWebhooks ? "bg-black text-white border-black" : "bg-white border-black/20 hover:bg-black/5"}`}
            >
              <Webhook className="w-3.5 h-3.5" /> Webhooks
            </button>
            <button
              data-testid="tab-admin-database"
              onClick={() => { setShowDatabase(true); setShowWebhooks(false); setShowReviews(false); setSelectedUserId(null); }}
              className={`inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest px-3 py-2 border ${showDatabase ? "bg-black text-white border-black" : "bg-white border-black/20 hover:bg-black/5"}`}
            >
              <Database className="w-3.5 h-3.5" /> Database
            </button>
            <button
              data-testid="tab-admin-reviews"
              onClick={() => { setShowReviews(true); setShowWebhooks(false); setShowDatabase(false); setSelectedUserId(null); }}
              className={`inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest px-3 py-2 border ${showReviews ? "bg-black text-white border-black" : "bg-white border-black/20 hover:bg-black/5"}`}
            >
              <MessageSquare className="w-3.5 h-3.5" /> Reviews
            </button>
          </div>
        </div>

        {showWebhooks && <WebhookInspector />}
        {showDatabase && <DatabaseBrowser />}
        {showReviews && <ReviewsAdmin />}

        {!showWebhooks && !showDatabase && !showReviews && !selectedUserId && (
          <>
            <section data-testid="admin-overview" className="mt-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
              <StatCard
                label="Total revenue"
                value={fmtINR(totalRev)}
                sub={`${overview?.subscription_payments_count || 0} subs + ${overview?.credit_purchases_count || 0} credit buys`}
                icon={Wallet}
                tone="blue"
              />
              <StatCard label="Subscription revenue" value={fmtINR(overview?.subscription_revenue_paise)} sub={`${overview?.subscription_payments_count || 0} payments`} icon={TrendingUp} />
              <StatCard label="Credits revenue" value={fmtINR(overview?.credits_revenue_paise)} sub={`${overview?.credit_purchases_count || 0} purchases`} icon={Coins} />
              <StatCard label="Active subs" value={overview?.active_subscriptions || 0} sub="paying users" icon={Users} />
              <StatCard label="Total users" value={overview?.total_users || 0} sub="incl. non-payers" icon={Users} />
            </section>

            <section className="mt-12">
              <div className="flex items-baseline justify-between">
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">/ Users</p>
                  <h2 className="font-heading font-black text-2xl mt-1">All accounts</h2>
                </div>
                <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                  {users.length} users
                </span>
              </div>

              <div className="mt-4 border-2 border-black bg-white divide-y divide-black/10">
                {users.map((u) => {
                  const s = u.subscription;
                  const q = s?.quota;
                  return (
                    <button
                      key={u.id}
                      data-testid={`admin-user-row-${u.id}`}
                      onClick={() => setSelectedUserId(u.id)}
                      className="w-full text-left p-4 hover:bg-[#DBEAFE]/40 transition-colors flex items-center justify-between gap-4"
                    >
                      <div className="min-w-0">
                        <p className="font-medium truncate">{u.email}</p>
                        <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 truncate">
                          {u.name || "—"} · joined {fmtDate(u.created_at)}
                        </p>
                      </div>
                      <div className="flex-shrink-0 flex items-center gap-4 text-right">
                        <div>
                          <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Plan</p>
                          <p className="text-sm font-medium">
                            {s ? (
                              <span className="inline-block px-2 py-0.5 bg-[#3B82F6] text-white font-mono text-[10px] uppercase tracking-widest">
                                {s.status}
                              </span>
                            ) : (
                              <span className="text-zinc-400">none</span>
                            )}
                          </p>
                        </div>
                        <div className="hidden sm:block">
                          <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Videos</p>
                          <p className="text-sm font-medium">
                            {q ? `${q.included_used}/${q.included_total}` : "—"}
                            {q?.extra_purchased ? <span className="text-zinc-500"> +{q.extra_purchased}</span> : null}
                          </p>
                        </div>
                        <ChevronRight className="w-4 h-4 text-zinc-400" />
                      </div>
                    </button>
                  );
                })}
              </div>
            </section>
          </>
        )}

        {!showWebhooks && !showDatabase && selectedUserId && <UserDetail userId={selectedUserId} onBack={() => setSelectedUserId(null)} />}
      </main>
    </div>
  );
}
